import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { CertificatesContext } from '../../contexts/certificates.context';
import { profileSelectors } from '../../store/selectors/profile';
import CertificatePreview from './CertificatePreview';
import { CapIcon } from './CertificateRow';

import './certificate-new.style.scss';

const HOMEWORK_DONE = 4;

const lessonsWord = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) return 'урок';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'уроки';

  return 'уроків';
};

const waitingHeadline = (lessonsLeft) => {
  const count = Number(lessonsLeft);

  if (Number.isFinite(count) && count > 0) {
    return `Ви зможете отримати ваш сертифікат через ${count} ${lessonsWord(count)}`;
  }

  return 'Ви зможете отримати ваш сертифікат, щойно виконаєте всі умови курсу';
};

const CertificateModalNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector(profileSelectors);
  const {
    byProgram,
    openProgram,
    setOpenProgram,
    issue,
    download,
    busy,
    error,
    notice,
    clearMessages,
  } = useContext(CertificatesContext);

  const isOpen = openProgram !== null;
  const item = isOpen ? byProgram[openProgram.id] ?? null : null;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        clearMessages();
        setOpenProgram(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, setOpenProgram, clearMessages]);

  if (!isOpen) {
    return null;
  }

  const close = () => {
    clearMessages();
    setOpenProgram(null);
  };

  const user = profile?.user || {};
  const studentName = [user.last_name, user.first_name].filter(Boolean).join(' ');
  const progress = Math.max(0, Math.min(100, Number(openProgram.progress) || 0));
  const lesson = openProgram.lesson || null;
  const certificates = item?.certificates || [];
  const isReady = Boolean(item?.can_issue);
  const isIssued = Boolean(item?.issued) || certificates.length > 0;
  const isWaiting = !isReady && !isIssued;
  const hasHomeworkToDo = Boolean(lesson?.is_show_homework) && Number(lesson?.homework_icon_color) !== HOMEWORK_DONE;

  const openHomework = () => {
    localStorage.setItem('event', JSON.stringify(lesson));
    localStorage.setItem('eventId', JSON.stringify(lesson.event_id));
    localStorage.setItem('realHomeworkId', JSON.stringify(lesson.homework_id));
    close();
    navigate('/homework', { state: { from: location.pathname } });
  };

  const title = isWaiting ? waitingHeadline(openProgram.lessonsLeft) : isIssued ? 'Ваш сертифікат готовий' : 'Сертифікат можна отримати!';
  const text = isWaiting ? '' : item?.summary;

  return (
    <div className="modal-info cmodal open" role="dialog" aria-modal="true" aria-label={title}>
      <div className="overlay" onClick={close}></div>

      <div className="cmodal__container">
        <button type="button" className="cmodal__close" onClick={close} aria-label="Закрити">×</button>

        <div className="cmodal__top">
          <div className="cmodal__info">
            <span className={`cmodal__badge ${isWaiting ? '' : 'is-ready'}`.trim()}>
              <CapIcon className="cmodal__badge-icon" />
            </span>
            <h2 className={`cmodal__title ${isWaiting ? 'cmodal__title--wait' : ''}`.trim()}>{title}</h2>
            {!isWaiting && (
              <p className="cmodal__course">
                {openProgram.title ? `Ваш сертифікат New Brain: ${openProgram.title}` : 'Ваш сертифікат New Brain'}
              </p>
            )}
            {text && <p className="cmodal__summary">{text}</p>}
          </div>

          <CertificatePreview studentName={studentName} />
        </div>

        {isWaiting && (
          <div className="cmodal__progress">
            <span className="cmodal__bar"><i style={{ width: `${progress}%` }}></i></span>
            <span className="cmodal__progress-text">Ваш прогрес — <b>{progress}%</b></span>
          </div>
        )}

        {certificates.length > 0 && (
          <ul className="cmodal-issued">
            {certificates.map((certificate) => (
              <li key={certificate.id} className="cmodal-issued__item">
                <span className="cmodal-issued__info">
                  <b>{certificate.number}</b>
                  <span>від {certificate.issued_date}</span>
                </span>
                <button
                  type="button"
                  className="cmodal-issued__link"
                  disabled={busy}
                  onClick={() => download(certificate)}
                >
                  Завантажити
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="cmodal__error">{error}</p>}
        {notice && <p className="cmodal__notice">{notice}</p>}

        <div className="cmodal__actions">
          {isReady && (
            <button
              type="button"
              className="cmodal__btn"
              disabled={busy}
              onClick={() => issue(item.program_id)}
            >
              {busy ? 'Зачекайте…' : 'Отримати сертифікат'}
            </button>
          )}

          {isWaiting && hasHomeworkToDo && (
            <button type="button" className="cmodal__btn" onClick={openHomework}>
              Зробити ДЗ 😉
            </button>
          )}

          {!isReady && !(isWaiting && hasHomeworkToDo) && (
            <button type="button" className="cmodal__btn cmodal__btn--light" onClick={close}>
              Зрозуміло
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateModalNew;
